import InfiniteScroll from 'react-infinite-scroll-component';

import PropTypes from 'prop-types';

import { Box, Typography } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import ThreadItemContainer from './ThreadItem/ThreadItemTemplateContainer';

import { dateToLocaleString, loadUser } from '../../../utils';
import {
  getMessagesHeight,
  getMessagesHeightMobile,
} from '../../Messages/utils';

import DateBubble from '../../Messages/components/DateBubble';
import { MessageItemSkeleton } from '../../Shared/skeletons';

const ThreadListComponent = ({
  chatMessages,
  loadChatMessages,
  nextChatMessages,
  isLoading,
  isMobileScreen,
  ...rest
}) => {
  const { t } = useTransl();

  const messagesContainerHeight = isMobileScreen
    ? getMessagesHeightMobile()
    : getMessagesHeight();

  // Only load 1 page of items at a time.
  // Pass an empty callback to InfiniteLoader in case it asks us to load more than once.
  const loadMoreItems = isLoading ? () => {} : loadChatMessages;

  // Every row is loaded except for a loader row (last one).
  const isItemLoaded = (index) =>
    !nextChatMessages || index < chatMessages.length;

  return (
    <Box
      display="flex"
      flexDirection="column-reverse"
      overflow="auto"
      id="currentThreadScrollable"
    >
      <InfiniteScroll
        dataLength={chatMessages.length}
        next={() => loadMoreItems()}
        hasMore={!isLoading && nextChatMessages}
        height={messagesContainerHeight}
        loader={
          chatMessages.length === 0 ? (
            Array.from({ length: 5 }).map((v, i) => (
              <MessageItemSkeleton key={i} />
            ))
          ) : (
            <MessageItemSkeleton />
          )
        }
        style={{
          display: 'flex',
          flexDirection: 'column-reverse',
          paddingRight: '1rem',
        }}
        inverse
        scrollableTarget="currentThreadScrollable"
        endMessage={
          chatMessages.length === 0 && isLoading ? (
            <MessageItemSkeleton />
          ) : chatMessages.length === 0 ? (
            <Typography align="center">{t('messages.noMessages')}</Typography>
          ) : null
        }
      >
        <Box
          display="flex"
          flexDirection="column-reverse"
          id="messages_scroll_area"
        >
          {chatMessages.map((message, i) => {
            if (!isItemLoaded(i + 1)) {
              return <div key={message?.id} />;
            }
            const currentMessageDate = dateToLocaleString(
              message?.createdAt,
              false,
              null,
              { weekday: 'short', month: 'short', day: 'numeric' },
              false,
            );
            const nextMessageDate = dateToLocaleString(
              chatMessages?.[i + 1]?.createdAt,
              false,
              null,
              { weekday: 'short', month: 'short', day: 'numeric' },
              false,
            );
            const isNewDate =
              currentMessageDate && currentMessageDate !== nextMessageDate;
            const date = isNewDate && currentMessageDate;
            const isUserMessage = message?.sender?.id === loadUser()?.id;
            return (
              <Box key={message?.id}>
                {date && <DateBubble date={date} />}
                <ThreadItemContainer
                  isUserMessage={isUserMessage}
                  message={message}
                  {...rest}
                />
              </Box>
            );
          })}
        </Box>
      </InfiniteScroll>
    </Box>
  );
};

ThreadListComponent.defaultProps = {
  chatMessages: [],
  nextChatMessages: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
  }),
};

ThreadListComponent.propTypes = {
  chatMessages: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  loadChatMessages: PropTypes.func.isRequired,
  nextChatMessages: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
};

export default ThreadListComponent;
